/*
 * siyou-creator-wall.js — control bar and poster facades for the creator wall.
 *
 * The track is a native scroll-snap strip and works without this file: touch,
 * trackpad and keyboard all scroll it already, and each tile's <noscript>
 * carries a plain <video> that renders and plays when scripting is off (a
 * <noscript> style hides the facades then). The control bar ships `hidden`
 * and is unhidden here. The poster facade ships visible and is a real link to
 * the MP4, so if this script fails to load with scripting on, a tile still
 * shows its poster and still plays — in the browser's own player.
 *
 * Deliberately does not auto-advance. DESIGN.md allows marquees as the only
 * continuous motion and requires a visible pause control for those; a
 * self-rotating gallery would be continuous motion with no pause control, and
 * would also pull a tile away from someone mid-playback.
 *
 * Listeners are removed in disconnectedCallback — several upstream components
 * leak listeners in the theme editor (AUDIT.md) and this one must not.
 */
// Guarded as a whole: a top-level `class` declaration throws "already been
// declared" if the section renders twice or the editor re-runs this file.
if (!customElements.get('siyou-creator-wall')) {
customElements.define('siyou-creator-wall', class SiyouCreatorWall extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cwall-track]');
    if (!this.track) return;

    this.nav = this.querySelector('[data-cwall-nav]');
    this.prevButton = this.querySelector('[data-cwall-prev]');
    this.nextButton = this.querySelector('[data-cwall-next]');
    this.thumb = this.querySelector('[data-cwall-thumb]');
    this.readout = this.querySelector('[data-cwall-pos]');
    this.rafId = 0;

    this.onPrev = () => this.page(-1);
    this.onNext = () => this.page(1);
    this.onScroll = () => this.scheduleSync();
    this.onResize = () => this.scheduleSync();
    this.onKeydown = (event) => this.keydown(event);
    this.onClick = (event) => this.mountVideo(event);
    // `play` does not bubble, so it is caught on the way down instead.
    this.onPlay = (event) => this.soloAudio(event);

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize, { passive: true });
    this.addEventListener('click', this.onClick);
    this.addEventListener('play', this.onPlay, true);

    this.sync();
  }

  disconnectedCallback() {
    if (!this.track) return;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    this.track.removeEventListener('scroll', this.onScroll);
    this.track.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('play', this.onPlay, true);
  }

  /*
   * Swap a poster facade for the real player. The tile's <noscript> holds the
   * only copy of the video markup: with scripting enabled the parser leaves it
   * as inert text, so nothing was fetched for it, and `.textContent` hands the
   * markup back at the moment the visitor actually asks to watch.
   */
  mountVideo(event) {
    const button = event.target.closest('[data-cwall-play]');
    if (!button || !this.contains(button)) return;
    // The facade may be a link to the MP4 (its no-script fallback).
    event.preventDefault();

    const frame = button.parentElement;
    const markup = frame && frame.querySelector('noscript');
    if (!markup) return;

    frame.insertAdjacentHTML('beforeend', markup.textContent);
    const video = frame.querySelector('video');
    button.remove();
    markup.remove();
    if (!video) return;

    video.controls = true;
    // Focus follows the control that replaced the button, or a keyboard user
    // is dropped back to the top of the document.
    video.focus();
    // ⚠️ Load explicitly before playing. A <video preload="none"> that is parsed
    // and played inside the same task has not yet run its resource selection
    // algorithm over the <source> children it was just given, and WebKit does
    // not start it on `play()` alone — the element sits at readyState 0 /
    // NETWORK_IDLE and never requests the MP4, while `paused` still flips to
    // false, so it reads as a started video that plays nothing. Measured on
    // iOS-engine WebKit at 390px: without this line readyState 0, videoWidth 0,
    // zero network requests for the file; with it readyState 4 and playing.
    // Chromium selects the source eagerly enough that it works either way,
    // which is why this never showed up on desktop.
    video.load();
    // A click is user activation, so this is allowed to play with sound. If the
    // browser refuses anyway the controls are already there to press.
    const started = video.play();
    if (started && typeof started.catch === 'function') started.catch(() => {});
  }

  /* One clip at a time — 24 tiles make overlapping audio easy to trigger. */
  soloAudio(event) {
    this.querySelectorAll('video').forEach((video) => {
      if (video !== event.target) video.pause();
    });
  }

  keydown(event) {
    // Only when the strip itself has focus: the same arrow keys seek inside a
    // focused <video>, and those events bubble up through here.
    if (event.target !== this.track) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.page(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.track.scrollLeft = event.key === 'Home' ? 0 : this.track.scrollWidth;
      this.sync();
    }
  }

  /*
   * A press moves one screenful, not one tile. At 24 tiles and five across,
   * stepping tile-by-tile is nineteen presses to reach the end; nothing is lost
   * by moving a whole window, because every clip in view is already directly
   * clickable — precision was never needed here, reach was. Whole tiles only,
   * so the strip always lands on a snap point (one tile per press on phone,
   * where only ~1.8 fit).
   *
   * ⚠️ Do NOT pass `behavior: 'smooth'` here. On a `scroll-snap-type: mandatory`
   * container Chrome cancels a programmatic smooth scroll when it coincides with
   * the real input that triggered it — so the arrows silently did nothing on a
   * genuine click while `button.click()` from the console worked fine. Leaving
   * `behavior` unset makes the scroll inherit `scroll-behavior` from CSS, which
   * is `auto` — an instant jump that cannot be cancelled.
   */
  page(direction) {
    const tile = this.tileWidth();
    const perPage = Math.max(1, Math.floor(this.track.clientWidth / tile));
    this.track.scrollBy({ left: direction * perPage * tile });
    // Sync here as well as on `scroll`: the scroll event is async and lagged
    // the enabled/disabled state by a full press, so the first press off either
    // end left the opposite arrow looking dead.
    this.sync();
    this.warm();
  }

  tileWidth() {
    const tile = this.track.querySelector('.bp-cwall__tile');
    return tile ? tile.getBoundingClientRect().width : this.track.clientWidth;
  }

  /* `sync` reads layout, so keep it to one call per frame while scrolling. */
  scheduleSync() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      this.sync();
      this.warm();
    });
  }

  /*
   * Fetch the posters just past the right edge, one scroll ahead of the
   * visitor.
   *
   * `loading="lazy"` inside a horizontal scroller is far more conservative
   * than it is down the page: measured on a throttled phone viewport, exactly
   * one tile was fetched ahead of the one in view. On the 1.35-across phone
   * carousel that means the strip shows a single clip, so EVERY swipe landed
   * on an empty ink frame that filled in a beat later — which reads as a video
   * that failed, not one that is loading.
   *
   * Promoting `loading` to eager on an already-parsed <img> starts its fetch
   * immediately, so this costs nothing until someone actually scrolls the
   * strip, and the section keeps its at-rest budget: no poster outside the
   * first screenful is requested by simply having the page open. Deliberately
   * NOT called from connectedCallback's sync() for that reason — connecting
   * the element must not pull posters for a section below the fold.
   */
  warm() {
    // A window resize also lands here (phone address-bar collapse fires one),
    // so bail unless the wall is actually on screen.
    const box = this.getBoundingClientRect();
    if (box.bottom <= 0 || box.top >= window.innerHeight) return;

    const tile = this.tileWidth();
    if (!tile) return;

    // One tile of lead, not three. A poster is ~170 KB of WebP and a phone
    // connection is the bottleneck here, not the scheduling: promoting three at
    // once put them in contention for the same pipe and each one landed LATER
    // than promoting a single tile did (measured on a throttled 390px
    // viewport). The placeholder underneath is paper for the same reason —
    // there is no warm distance that wins this race outright, so the frame a
    // visitor swipes onto has to be presentable while it is still empty.
    const first = Math.floor(this.track.scrollLeft / tile);
    const last = first + Math.ceil(this.track.clientWidth / tile) + 1;
    const tiles = this.track.children;
    for (let i = Math.max(0, first); i <= last && i < tiles.length; i += 1) {
      const poster = tiles[i].querySelector('img[loading="lazy"]');
      if (poster) poster.loading = 'eager';
    }
  }

  sync() {
    // 2px of slack: fractional tile widths leave sub-pixel scroll remainders,
    // which would otherwise keep the end arrow enabled forever.
    const max = this.track.scrollWidth - this.track.clientWidth;
    const overflows = max > 2;

    if (this.nav) this.nav.hidden = !overflows;
    if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= max - 2;
    if (!overflows) return;

    const visible = this.track.clientWidth / this.track.scrollWidth;
    const offset = this.track.scrollLeft / this.track.scrollWidth;
    this.style.setProperty('--bp-cwall-thumb-w', `${(visible * 100).toFixed(3)}%`);
    this.style.setProperty('--bp-cwall-thumb-x', `${(offset * 100).toFixed(3)}%`);

    // The readout counts tiles, not pages — it names the clip at the left edge.
    if (!this.readout) return;
    const tile = this.tileWidth();
    const index = tile ? Math.round(this.track.scrollLeft / tile) + 1 : 1;
    this.readout.textContent = String(index).padStart(2, '0');
  }
});
}
